import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Per-route SEO + canonical tag helper.
 * Drop one of these at the top of every public marketing page so each route
 * declares its own <title>, <meta description>, og:url, og:image, and
 * canonical URL.
 *
 * og:image is what social platforms (iMessage, Facebook, LinkedIn,
 * Twitter, Slack, Reddit) render as a preview when someone shares the URL.
 * For Compass article routes, pass the dynamic OG endpoint:
 *   ogImage={`${SITE}/api/og/article/${articleId}.png`}
 * For all other pages, omit ogImage to use the site-wide default card.
 *
 * IMPORTANT — Curtain Navigation contract:
 * The CurtainNavigation component mounts ALL 8 marketing pages simultaneously
 * (Home, About Us, Founders, CADENCE Promise, Why We Exist, Pricing,
 * Membership, Compass). That means every page's <SEO> renders at once and
 * fights for the same Helmet meta tags. To pick a winner deterministically,
 * we only emit meta tags when the page's declared `path` matches the
 * currently-active URL. Otherwise we render nothing.
 *
 * LAUNCHED BEHAVIOR (2026-02-08): All public marketing pages are
 * indexable by default. Only PRIVATE routes (/admin/*, /portal/*,
 * /login, /guest-intake) emit a noindex tag so they never appear in
 * search results.
 */
const SITE = 'https://www.cadencemedicalconsulting.com';
const DEFAULT_OG_IMAGE = `${SITE}/api/og/default.png`;

// Private routes that must NEVER be indexed (auth-walled or app shell)
const NON_INDEXABLE_PREFIXES = ['/admin', '/portal', '/login', '/guest-intake'];

// Human-readable breadcrumb labels for every public marketing route.
// Used to auto-derive the BreadcrumbList JSON-LD when a page doesn't
// pass its own `breadcrumbs` prop. Home is the implicit root and is
// prepended automatically — don't add it here.
const ROUTE_BREADCRUMB_LABELS = {
  '/coming-soon': 'Coming Soon',
  '/home': 'Home',
  '/about-us': 'About Us',
  '/founders': 'Our Founders',
  '/cadence-promise': 'The CADENCE Promise',
  '/our-story': 'Our Story',
  '/why-we-exist': 'Why We Exist',
  '/pricing': 'Pricing',
  '/membership': 'Membership',
  '/compass-preview': 'CADENCE Compass',
  '/demo': 'See How It Works',
  '/faq': 'Frequently Asked Questions',
  '/contact': 'Contact',
  '/hipaa': 'HIPAA Notice',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
};

const isPathIndexable = (path) =>
  !NON_INDEXABLE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

// Build the BreadcrumbList JSON-LD payload. Returns null when the
// route is Home (no breadcrumb trail needed for the root).
const buildBreadcrumbJsonLd = (path, overrides) => {
  // Home / root => no breadcrumbs, Google handles this implicitly.
  if (path === '/' || path === '/home') return null;

  const trail = overrides && overrides.length > 0
    ? overrides
    : [{ name: ROUTE_BREADCRUMB_LABELS[path] || 'Page', path }];

  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
    ...trail.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 2,
      name: crumb.name,
      item: `${SITE}${crumb.path}`,
    })),
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

export default function SEO({ title, description, path = '/', ogImage, breadcrumbs }) {
  const location = useLocation();

  // Curtain pages all mount at once. Only the SEO instance whose path
  // matches the URL the visitor is actually on should populate Helmet.
  if (location.pathname !== path) {
    return null;
  }

  const url = `${SITE}${path}`;
  const fullTitle = title?.includes('CADENCE')
    ? title
    : `${title} | CADENCE Medical Consulting`;
  const image = ogImage || DEFAULT_OG_IMAGE;
  const isIndexable = isPathIndexable(path);
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(path, breadcrumbs);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Launched 2026-02-08: every public marketing route is
          indexable; only the private app shell (/admin, /portal,
          /login, /guest-intake) emits noindex so it stays out of
          search engines. */}
      <meta
        name="robots"
        content={isIndexable ? 'index, follow, max-image-preview:large, max-snippet:-1' : 'noindex, nofollow'}
      />
      <meta
        name="googlebot"
        content={isIndexable ? 'index, follow' : 'noindex, nofollow'}
      />
      <meta
        name="bingbot"
        content={isIndexable ? 'index, follow' : 'noindex, nofollow'}
      />

      {/* Open Graph (Facebook, LinkedIn, iMessage, Slack, Discord, ...) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CADENCE Medical Consulting" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter / X — summary_large_image renders the big 1200x630 card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <link rel="canonical" href={url} />

      {/* BreadcrumbList JSON-LD — gives Google the multi-line
          rich result (Home › Section › Page) on every public page
          except Home itself. Auto-derived from ROUTE_BREADCRUMB_LABELS
          unless the page passes a custom `breadcrumbs` trail. */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}
    </Helmet>
  );
}
